import {
  COMBO_BLOOM_PALETTE,
  COMBO_HUD_BASE_FONT_PX,
  COMBO_UNDERLINE_MIN,
  HP_BAR_HEIGHT_PX,
  HP_BAR_TOP_PX,
  HP_DANGER_PULSE_THRESHOLD,
} from './pianoRenderConstants';
import { comboTier, withAlpha } from './pianoRenderColour';
import type { PianoLayout } from '../pianoPlayfieldGeometry';
import type { PianoRenderInput } from './pianoRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * HP bar + score + combo readout. Owns the combo-pulse state (bumps
 * on every combo increment, decays over ~250ms) so the score/combo
 * scale-up animations sync with engine input without the orchestrator
 * juggling per-HUD-element timers.
 */
export class HudRenderer {
  /** HUD combo-bump animation timer; bumps to 1 on combo increment, decays. */
  private comboPulseT = 0;
  private lastShownCombo = 0;

  reset(): void {
    this.comboPulseT = 0;
    this.lastShownCombo = 0;
  }

  /**
   * Update HUD combo pulse state. Detects combo increment (bumps
   * `comboPulseT` to 1 so the readout scales / glows on the next
   * frame) and decays back to 0 over ~250ms.
   */
  advance(currentCombo: number, dt: number): void {
    if (currentCombo > this.lastShownCombo) {
      this.comboPulseT = 1;
    }
    this.lastShownCombo = currentCombo;
    if (dt > 0) {
      const decayRate = 4;
      this.comboPulseT = Math.max(0, this.comboPulseT - decayRate * dt);
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    input: PianoRenderInput,
    layout: PianoLayout,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const { chart, state, displayedScore } = input;
    const palette = chart.palette;
    // HP bar across the top of the playing field (not the full canvas) —
    // keeps the HUD locked to the centred play surface so wide canvases
    // don't strand the bar over empty margins. Skipped entirely in
    // instadeath runs: with no HP buffer the bar would sit full until a
    // single miss empties it, which reads as a bug rather than a gauge.
    if (!input.instadeath) {
      const hpFrac = Math.max(0, Math.min(1, state.hp / state.maxHp));
      const barX = layout.lane0X;
      const barW = layout.laneStackWidth;
      ctx.fillStyle = withAlpha(palette.bgAccent, 0.85);
      ctx.fillRect(barX, HP_BAR_TOP_PX, barW, HP_BAR_HEIGHT_PX);

      // Bar fill: green above the colour-flip threshold, red below. Under
      // the danger threshold the red fill pulses via a glow shadow so
      // urgency reads pre-attentively even if the player's eyes are on
      // the lanes. Reduced-motion drops the pulse to a steady glow so
      // the cue remains without flicker. Low-tier devices skip the glow
      // entirely — the colour flip carries the danger signal on its own.
      const lowHp = hpFrac <= 0.4;
      const danger = hpFrac <= HP_DANGER_PULSE_THRESHOLD;
      const dangerPulse =
        danger && !reducedMotion ? 0.5 + 0.5 * Math.sin(performance.now() * 0.015) : 0;
      ctx.save();
      if (danger && quality.chromeShadows) {
        ctx.shadowColor = palette.hazard;
        ctx.shadowBlur = reducedMotion ? 10 : 8 + dangerPulse * 18;
      }
      ctx.fillStyle = lowHp ? palette.hazard : palette.padPerfect;
      ctx.fillRect(barX, HP_BAR_TOP_PX, barW * hpFrac, HP_BAR_HEIGHT_PX);
      ctx.restore();
    }

    // Score readout (top-right of the playing field). Slight scale bump
    // on each increment via comboPulseT plus a brief gold glow — the
    // eye snaps to the score on every hit, then the readout settles
    // back to the calm white state at rest.
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    const scoreScale = 1 + this.comboPulseT * 0.08;
    const scoreSize = Math.round(28 * scoreScale);
    ctx.font = `bold ${scoreSize}px system-ui, sans-serif`;
    ctx.fillStyle = palette.ui;
    const scoreText = Math.round(displayedScore).toLocaleString();
    const scoreY = HP_BAR_TOP_PX + HP_BAR_HEIGHT_PX + 6;
    const hudRightX = layout.lane0X + layout.laneStackWidth;
    if (!reducedMotion && this.comboPulseT > 0 && quality.chromeShadows) {
      ctx.save();
      ctx.shadowColor = '#ffd84d';
      ctx.shadowBlur = this.comboPulseT * 20;
      ctx.fillText(scoreText, hudRightX, scoreY);
      ctx.restore();
    } else {
      ctx.fillText(scoreText, hudRightX, scoreY);
    }

    // Combo readout — combo number + multiplier suffix, scaled and
    // coloured by combo tier. Bumps + glows briefly on every increment
    // so a fast stream feels like a building chain. A streak underline
    // grows wider with combo and thicker with tier to give a "this is
    // getting serious" prestige cue once the player passes
    // COMBO_UNDERLINE_MIN.
    if (state.combo > 1) {
      this.drawComboReadout(ctx, state.combo, scoreY, scoreSize, hudRightX, reducedMotion, quality);
    }
  }

  private drawComboReadout(
    ctx: CanvasRenderingContext2D,
    combo: number,
    scoreY: number,
    scoreSize: number,
    hudRightX: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const tier = comboTier(combo);
    const tierColour = COMBO_BLOOM_PALETTE[tier];
    const comboScale = 1 + this.comboPulseT * 0.22;
    const comboSize = Math.round(COMBO_HUD_BASE_FONT_PX * comboScale);
    const comboY = scoreY + scoreSize + 10;
    ctx.font = `bold ${comboSize}px system-ui, sans-serif`;
    ctx.fillStyle = tierColour;
    const comboLabel = `${combo}x`;
    const allowGlow = !reducedMotion && quality.chromeShadows;
    const baseGlow = allowGlow && tier > 0 ? 4 + tier * 4 : 0;
    const pulseGlow = allowGlow ? this.comboPulseT * 16 : 0;
    const totalGlow = baseGlow + pulseGlow;
    if (totalGlow > 0) {
      ctx.save();
      ctx.shadowColor = tierColour;
      ctx.shadowBlur = totalGlow;
      ctx.fillText(comboLabel, hudRightX, comboY);
      ctx.restore();
    } else {
      ctx.fillText(comboLabel, hudRightX, comboY);
    }

    if (combo >= COMBO_UNDERLINE_MIN) {
      const labelW = ctx.measureText(comboLabel).width;
      const underlineFrac = Math.min(1, combo / 100);
      const underlineW = labelW * (0.3 + 0.7 * underlineFrac);
      const underlineH = 2 + tier;
      const underlineY = comboY + comboSize + 4;
      if (allowGlow && tier > 0) {
        ctx.save();
        ctx.shadowColor = tierColour;
        ctx.shadowBlur = 4 + tier * 3;
        ctx.fillStyle = withAlpha(tierColour, 0.85);
        ctx.fillRect(hudRightX - underlineW, underlineY, underlineW, underlineH);
        ctx.restore();
      } else {
        ctx.fillStyle = withAlpha(tierColour, 0.85);
        ctx.fillRect(hudRightX - underlineW, underlineY, underlineW, underlineH);
      }
    }
  }
}
